import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControlLabel,
  Switch,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, WhatsApp as WhatsAppIcon, Facebook as FacebookIcon, Instagram as InstagramIcon, Forum as ForumIcon, Groups as GroupsIcon } from '@mui/icons-material';
import { collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { SocialGroup, SocialType } from '../../types/social';
import PageLayout from '../../components/PageLayout';
import PageTitle from '../../components/PageTitle';
import { adminStyles } from '../../theme/theme';

// Mapping des types de réseaux sociaux
const SOCIAL_TYPES = {
  whatsapp: 'WhatsApp',
  facebook: 'Facebook',
  discord: 'Discord',
  instagram: 'Instagram'
};

export const SocialGroups = () => {
  const [groups, setGroups] = useState<SocialGroup[]>([]);
  const [open, setOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<SocialGroup | null>(null);
  const [formData, setFormData] = useState<Omit<SocialGroup, 'id' | 'createdAt' | 'updatedAt'>>({
    name: '',
    description: '',
    url: '',
    type: 'whatsapp',
    forAdmin: false,
    active: true,
    expiresAt: null,
  });

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'socials'), (snapshot) => {
      const groupsData = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          expiresAt: data.expiresAt?.toDate() || null,
        } as SocialGroup;
      });

      // Trier par date de création (plus récent en premier)
      groupsData.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

      setGroups(groupsData);
    });

    return () => unsubscribe();
  }, []);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingGroup(null);
    setFormData({
      name: '',
      description: '',
      url: '',
      type: 'whatsapp',
      forAdmin: false,
      active: true,
      expiresAt: null,
    });
  };

  const handleEdit = (group: SocialGroup) => {
    setEditingGroup(group);
    setFormData({
      name: group.name,
      description: group.description,
      url: group.url,
      type: group.type,
      forAdmin: group.forAdmin,
      active: group.active,
      expiresAt: group.expiresAt,
    });
    setOpen(true);
  };

  const handleDelete = async (groupId: string) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce groupe ?')) {
      try {
        await deleteDoc(doc(db, 'socials', groupId));
      } catch (error) {
        console.error('Erreur lors de la suppression du groupe:', error);
      }
    }
  };

  const handleSubmit = async () => {
    try {
      const now = new Date();
      const groupData = {
        ...formData,
        createdAt: editingGroup ? Timestamp.fromDate(editingGroup.createdAt) : Timestamp.fromDate(now),
        updatedAt: Timestamp.fromDate(now),
      };

      if (editingGroup) {
        const docRef = doc(db, 'socials', editingGroup.id);
        await updateDoc(docRef, groupData);
      } else {
        await addDoc(collection(db, 'socials'), groupData);
      }

      handleClose();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    }
  };

  return (
    <PageLayout>
      <PageTitle
        title="Gestion des groupes de communication"
        icon={<GroupsIcon />}
        action={
          <Button
            variant="contained"
            color="primary"
            onClick={handleClickOpen}
          >
            + Ajouter un groupe
          </Button>
        }
      />

      <TableContainer component={Paper} sx={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nom</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Lien</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Admin</TableCell>
              <TableCell>Actif</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.map((group) => (
              <TableRow key={group.id}>
                <TableCell>{group.name}</TableCell>
                <TableCell>{group.description}</TableCell>
                <TableCell>
                  <a
                    href={group.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#ffcc00', textDecoration: 'none', fontWeight: 'bold' }}
                    onMouseOver={(e) => e.currentTarget.style.textDecoration = 'underline'}
                    onMouseOut={(e) => e.currentTarget.style.textDecoration = 'none'}
                  >
                    {group.url}
                  </a>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {group.type === 'whatsapp' && <WhatsAppIcon />}
                    {group.type === 'facebook' && <FacebookIcon />}
                    {group.type === 'discord' && <ForumIcon />}
                    {group.type === 'instagram' && <InstagramIcon />}
                    {SOCIAL_TYPES[group.type]}
                  </Box>
                </TableCell>
                <TableCell>{group.forAdmin ? 'Oui' : 'Non'}</TableCell>
                <TableCell>{group.active ? 'Oui' : 'Non'}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 1 }}>
                    <IconButton
                      onClick={() => handleEdit(group)}
                      sx={{
                        ...adminStyles.glassButton,
                        '&:hover': {
                          ...adminStyles.glassButton['&:hover'],
                          bgcolor: 'primary.main'
                        }
                      }}
                      size="small"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      onClick={() => group.id && handleDelete(group.id)}
                      sx={{
                        ...adminStyles.glassButton,
                        '&:hover': {
                          ...adminStyles.glassButton['&:hover'],
                          bgcolor: 'error.main'
                        }
                      }}
                      size="small"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{editingGroup ? 'Modifier le groupe' : 'Ajouter un groupe'}</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Nom"
            fullWidth
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
          <TextField
            margin="dense"
            label="URL"
            fullWidth
            value={formData.url}
            onChange={(e) => setFormData({ ...formData, url: e.target.value })}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Type</InputLabel>
            <Select
              value={formData.type}
              label="Type"
              onChange={(e) => setFormData({ ...formData, type: e.target.value as SocialType })}
            >
              {Object.entries(SOCIAL_TYPES).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {value === 'whatsapp' && <WhatsAppIcon />}
                    {value === 'facebook' && <FacebookIcon />}
                    {value === 'discord' && <ForumIcon />}
                    {value === 'instagram' && <InstagramIcon />}
                    {label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={formData.forAdmin}
                onChange={(e) => setFormData({ ...formData, forAdmin: e.target.checked })}
              />
            }
            label="Réservé aux administrateurs"
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.active}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
              />
            }
            label="Actif"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Annuler</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {editingGroup ? 'Modifier' : 'Ajouter'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  );
};

export default SocialGroups;
